import { createApi } from '@reduxjs/toolkit/query/react'
import type { RiderVenueAnswers } from '@tunasong/schemas'
import baseQuery from './base-query.js'

/**
 * One access link as the list returns it to this caller.
 *
 * Declared here rather than imported from `@tunasong/access-links`: that is a backend package and
 * `lib/redux` does not, and should not, depend on one. The server projects per caller: `lastUsedAt`
 * is present only on the caller's own links.
 */
export interface AccessLinkView {
  linkId: string
  name: string
  createdAt: string
  createdBy: string
  /** When the link stops working; absent for calendar links */
  expiresAt?: string
  /** Set on a tombstone: the link stopped working and its row stays for the dead-row grace period */
  revokedAt?: string
  /** Its creator left (the cascade), or a member turned it off */
  revokedReason?: 'creator-left' | 'turned-off'
  /** Present only on the caller's own links */
  lastUsedAt?: string
}

/**
 * The client-visible result of minting or regenerating an access link.
 */
export interface CreatedAccessLink {
  /**
   * The link URL, token included.
   *
   * @important The create and regenerate responses are the ONLY ones in the whole access-link
   * surface that carry the token. The store keeps a hash, so it can never be retrieved again: a
   * caller that does not put this in front of the user has lost it, and the only remedy is another link.
   */
  url: string
  link: AccessLinkView
}

const linksTag = (entityId: string) => ({ type: 'AccessLinks' as const, id: entityId })
const answersTag = (riderId: string) => ({ type: 'VenueAnswers' as const, id: riderId })

/**
 * Access-link management (GH #1853), mounted at the `links` mapping key (`access-links-stack.ts`).
 * These are ordinary JWT routes: a user manages links while logged in. Only the token routes (the
 * calendar feed, the rider venue sheet) consume the link authorizer.
 */
export const accessLinksApi = createApi({
  reducerPath: 'access-links-api',
  baseQuery,
  tagTypes: ['AccessLinks', 'VenueAnswers'],
  endpoints: builder => ({
    listAccessLinks: builder.query<AccessLinkView[], { entityId: string }>({
      query: ({ entityId }) => `links/${entityId}`,
      transformResponse: (response: { links: AccessLinkView[] }) => response.links,
      providesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    createAccessLink: builder.mutation<CreatedAccessLink, { entityId: string; name: string; expiresAt?: string }>({
      query: ({ entityId, name, expiresAt }) => ({
        url: `links/${entityId}`,
        method: 'POST',
        body: expiresAt ? { name, expiresAt } : { name },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    regenerateAccessLink: builder.mutation<CreatedAccessLink, { entityId: string; linkId: string }>({
      query: ({ entityId, linkId }) => ({
        url: `links/${entityId}/${linkId}`,
        method: 'PATCH',
        body: { action: 'regenerate' },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    /**
     * Turn a link off: it stops working at once, and its row stays as a tombstone, so what came with it (a venue's
     * answers) is kept until the row is removed with `deleteAccessLink`.
     */
    turnOffAccessLink: builder.mutation<{ link: AccessLinkView }, { entityId: string; linkId: string }>({
      query: ({ entityId, linkId }) => ({
        url: `links/${entityId}/${linkId}`,
        method: 'PATCH',
        body: { action: 'turn-off' },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    /**
     * A rider's venue answers, one set per link that still exists (Rider Phase 3b). The rider venue API, mounted at
     * `rider`; READ on the rider.
     */
    listRiderVenueAnswers: builder.query<RiderVenueAnswers['links'], { riderId: string }>({
      query: ({ riderId }) => `rider/entity/${riderId}/answers`,
      transformResponse: (response: RiderVenueAnswers) => response.links,
      providesTags: (_result, _error, { riderId }) => [answersTag(riderId)],
    }),
    /** Remove takes a link's answers with it: called before `deleteAccessLink` */
    deleteRiderVenueAnswers: builder.mutation<void, { riderId: string; linkId: string }>({
      query: ({ riderId, linkId }) => ({
        url: `rider/entity/${riderId}/answers/${linkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { riderId }) => [answersTag(riderId)],
    }),
    /**
     * A turned-off link's answers expire with its row, 30 days after it was turned off. Turn off calls this right
     * after `turnOffAccessLink`; the server derives the expiry.
     */
    expireRiderVenueAnswers: builder.mutation<void, { riderId: string; linkId: string }>({
      query: ({ riderId, linkId }) => ({
        url: `rider/entity/${riderId}/answers/${linkId}/expiry`,
        method: 'PUT',
      }),
    }),
    deleteAccessLink: builder.mutation<void, { entityId: string; linkId: string }>({
      query: ({ entityId, linkId }) => ({
        url: `links/${entityId}/${linkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
  }),
})

export const {
  useCreateAccessLinkMutation,
  useListAccessLinksQuery,
  useRegenerateAccessLinkMutation,
  useTurnOffAccessLinkMutation,
  useDeleteAccessLinkMutation,
  useListRiderVenueAnswersQuery,
  useDeleteRiderVenueAnswersMutation,
  useExpireRiderVenueAnswersMutation,
} = accessLinksApi
